import { useMemo } from 'react';
import { observer } from 'mobx-react';
import compact from 'lodash/compact';

import styles from './MedicalCaseMultiSelect.module.scss';

import { MultiSelect, OptionType } from '@components/Select';
import { translate } from '@chaika/i18n/helper';
import { Nullable } from 'chaika-frontend-types/backend';
import { MedicalCaseData } from 'chaika-frontend-types/frontend-cms';
import { MultiSelectModifiers } from '@components/Select/MultiSelect/MultiSelect';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { MedicalCaseStatus } from 'chaika-enums';

interface Props {
    canEdit?: boolean;
    disabled?: boolean;
    medicalCaseValues?: { id: string; value: string; onClick(): void }[];
    name?: string;
    patientId?: string;
    mergedPatientIds?: string[];
    entryId?: string;
    modifiers?: MultiSelectModifiers[];
    minSelected?: number;
    medicalCaseIds: string[];
    medicalCases: readonly MedicalCaseData[];
    onChangeMedicalCaseIds(selectedItem: Nullable<OptionType<string>[]>, name?: string): void;
}

export const MedicalCaseMultiSelect = observer(
    ({
        medicalCaseIds,
        patientId,
        mergedPatientIds,
        entryId,
        name,
        modifiers,
        minSelected,
        onChangeMedicalCaseIds,
        medicalCases,
        disabled,
        medicalCaseValues,
        canEdit,
    }: Props) => {
        const medicalCasesOptions = useMemo(() => {
            const ehrIds: string[] = [];
            if (patientId) {
                ehrIds.push(patientId);
            }
            if (mergedPatientIds) {
                ehrIds.push(...mergedPatientIds);
            }

            const filteredMedicalCases = medicalCases.filter(medicalCase => {
                const byEhr = ehrIds.length > 0 ? ehrIds.includes(medicalCase.ehrId) : true;
                const byEntry = entryId
                    ? medicalCase.parentEntryId === entryId || (medicalCase.addedEntryIds?.includes(entryId) ?? false)
                    : true;

                return byEhr && byEntry;
            });

            const selectedMedicalCases = medicalCaseIds
                .map(id => medicalCases.find(medicalCase => medicalCase.id === id))
                .filter(it => !filteredMedicalCases.some(entryMedicalCase => entryMedicalCase.id === it?.id));

            return compact([...filteredMedicalCases, ...selectedMedicalCases])
                .filter(medicalCase => {
                    return medicalCaseIds.includes(medicalCase.id) || medicalCase.status === MedicalCaseStatus.Open;
                })
                .map(it => ({
                    label: it.name,
                    value: it.id,
                }));
        }, [patientId, mergedPatientIds, medicalCases, entryId, medicalCaseIds]);

        return (
            <>
                {canEdit ? (
                    <MultiSelect
                        placeholder={translate('chooseFromList')}
                        name={name}
                        disabled={disabled}
                        options={medicalCasesOptions}
                        modifiers={modifiers}
                        minSelected={minSelected}
                        title={translate('medicalAssistanceEpisodes')}
                        onChange={onChangeMedicalCaseIds}
                        selectedValues={medicalCaseIds}
                    />
                ) : (
                    <>
                        {medicalCaseValues && medicalCaseValues?.length > 0 && (
                            <div className={styles.medicalCasesView}>
                                <FieldTitle
                                    theme={FieldTitleTheme.WithFlex}
                                    title={translate('medicalAssistanceEpisodes')}
                                />
                                <div className={styles.fields}>
                                    {medicalCaseValues?.map(it => {
                                        return (
                                            <div
                                                key={it.id}
                                                className={styles.item}
                                                onClick={it.onClick}
                                            >
                                                {it.value}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    },
);
